import React, { ChangeEvent, ForwardedRef, forwardRef, useState, KeyboardEvent } from 'react';
import cn from 'classnames';
import InputMask, { ReactInputMask } from 'react-input-mask';

import { IconNameType } from '@/components/ui/icons/icon-name.type';
import { CircleButton } from '@/components/ui/index';

type InputProps = {
  mask?: string;
  label?: string;
  defaultValue?: string | number;
  placeholder?: string;
  disabled?: boolean;
  name?: string;
  error?: string;
  step?: number;
  type?: 'email' | 'text' | 'number' | 'color' | 'password';
  icon?: IconNameType;
  secondIcon?: IconNameType;
  iconColor?: 'primary' | 'default';
  min?: number | string;
  max?: number | string;
  maxLength?: number;
  onClick?: () => void;
  secondOnClick?: () => void;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onPaste?: (event: React.ClipboardEvent<HTMLInputElement>) => void;
  formatChars?: { [x: string]: string };
  iconHintTitle?: string;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  stopPropagation?: boolean;
};

const Input = forwardRef((props: InputProps, forwardedRef) => {
  const {
    mask,
    label,
    placeholder,
    name,
    type = 'text',
    step,
    defaultValue,
    disabled = false,
    error,
    icon,
    secondIcon,
    iconColor = 'default',
    onChange,
    secondOnClick,
    onClick,
    onPaste,
    onKeyDown,
    iconHintTitle,
    stopPropagation,
    ...remainingProps
  } = props;
  const [, setInputValue] = useState<string | number>('');

  function handleOnChange(event: ChangeEvent<HTMLInputElement>) {
    setInputValue(event.target.value);
    onChange?.(event);
  }
  function handleOnPaste(event: React.ClipboardEvent<HTMLInputElement>) {
    setInputValue(event.clipboardData.getData('text'));
    onPaste?.(event);
  }

  const inputClassName = ({ error, disabled }: { error: boolean; disabled: boolean }) =>
    cn(
      'border-[1px]',
      disabled ? 'text-2color bg-b_light' : 'text-0color',
      error ? 'border-basic_red' : 'border-b_dark',
      'text-sm',
      'relative',
      'bg-white',
      'rounded-defaultR',
      'flex',
      'items-center',
      'block',
      'w-full',
      'h-[34px]',
    );

  const iconColorList = {
    default: 'text-3color',
    primary: 'text-action',
    white: 'text_white',
  };

  const inputProps = {
    type,
    step,
    name,
    placeholder,
    className:
      'border-[0px] w-full h-[32px] px-[12px] outline-none outline-0 ring-0 rounded-defaultR focus:ring-0',
    onChange: handleOnChange,
    onPaste: handleOnPaste,
    disabled,
    onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => onKeyDown?.(event),
  };

  return (
    <div className="w-full relative">
      {label && <label className="block ml-[6px] mb-[2px] text-sm font-[500] ">{label}</label>}
      <div className={inputClassName({ error: !!error, disabled })}>
        {mask ? (
          <InputMask
            onClick={(e) => e.stopPropagation()}
            mask={mask}
            ref={forwardedRef as ForwardedRef<ReactInputMask>}
            value={defaultValue}
            {...inputProps}
            {...remainingProps}
          />
        ) : (
          <input
            onClick={(e) => e.stopPropagation()}
            ref={forwardedRef as ForwardedRef<HTMLInputElement>}
            value={defaultValue}
            {...inputProps}
            {...remainingProps}
          />
        )}

        {icon && (
          <div className="flex justify-center gap-[5px] pr-[3px] bg-transparent pl-[0px]">
            <CircleButton
              hintTitle={iconHintTitle}
              onClick={onClick?.bind(null)}
              icon={icon}
              className={cn(iconColorList[iconColor], 'items-center flex')}
              size={14}
              stopPropagation={stopPropagation}
            />
            {secondIcon && (
              <div className="rounded-[10px] bg-action">
                <CircleButton
                  stopPropagation={stopPropagation}
                  onClick={secondOnClick?.bind(null)}
                  icon={secondIcon}
                  className={` text-white`}
                  size={14}
                />
              </div>
            )}
          </div>
        )}
      </div>
      {!!error && <div className="mt-[1px] whitespace-nowrap text-sm text-basic_red">{error}</div>}
    </div>
  );
});

Input.displayName = 'Input';

export default Input;
